import { z } from "zod";
import {
  APPROVAL_DECISIONS,
  CHANGE_CLASSES,
  DATA_CLASSES,
  FLOWS,
  MISSION_STATUSES,
  PRIORITIES,
  RISK_LEVELS,
  ROLES,
  STAGES,
  type Indicator,
  type MissionStatus,
  type Stage,
} from "./enums.js";
import { REVIEW_MODES } from "./review.js";
import type { DeliverySummary, PlanningSummary } from "./summaries.js";

/** Entradas de la API (validadas en backend, reutilizadas por el formulario del frontend). */

export const loginInput = z.object({ email: z.email(), password: z.string().min(1) });

export const createMissionInput = z.object({
  title: z.string().trim().min(3).max(160),
  objective: z.string().trim().min(10).max(8000),
  projectId: z.uuid(),
  repositoryId: z.uuid(),
  priority: z.enum(PRIORITIES).default("medium"),
  changeClass: z.enum(CHANGE_CLASSES).optional(),
  budgetTokens: z.number().int().positive().optional(),
  budgetAmount: z.number().positive().optional(),
  maxRuns: z.number().int().min(1).max(20).optional(),
  maxRetries: z.number().int().min(0).max(10).optional(),
  autoStart: z.boolean().default(true),
});
export type CreateMissionInput = z.infer<typeof createMissionInput>;

export const decisionInput = z.object({
  decision: z.enum(APPROVAL_DECISIONS),
  comment: z.string().trim().max(4000).optional(),
  /** Versión del plan que el usuario estaba viendo (aprobación de plan). */
  planVersion: z.number().int().positive().optional(),
  /** SHA exacto revisado (aprobación de entrega). */
  headSha: z.string().regex(/^[0-9a-f]{7,64}$/i).optional(),
});
export type DecisionInput = z.infer<typeof decisionInput>;

/** Una persona define (o corrige) el alcance de un plan que no lo trae verificable. Crea una versión nueva del plan que debe aprobarse. */
export const defineScopeInput = z.object({
  plannedFiles: z.array(z.string().trim().min(1).max(300)).min(1).max(200),
  acceptanceCriteria: z.array(z.string().trim().min(1).max(500)).min(1).max(30),
  problem: z.string().trim().min(3).max(2000).optional(),
  proposedSolution: z.string().trim().min(3).max(4000).optional(),
  comment: z.string().trim().max(2000).optional(),
});
export type DefineScopeInput = z.infer<typeof defineScopeInput>;

/** Decisión sobre ficheros fuera del plan (autorización de alcance). `files` debe ser un subconjunto de los pendientes. */
export const scopeDecisionInput = z.object({
  decision: z.enum(["approved", "changes_requested"]),
  files: z.array(z.string().trim().min(1)).min(1).max(200),
  comment: z.string().trim().max(4000).optional(),
});
export type ScopeDecisionInput = z.infer<typeof scopeDecisionInput>;

export const instructionInput = z.object({ text: z.string().trim().min(3).max(8000) });
export const moveRequestInput = z.object({ toStage: z.enum(STAGES) });

export const companyInput = z.object({
  name: z.string().trim().min(2).max(60),
  slug: z.string().trim().min(2).max(32).optional(),
  kind: z.enum(["holding", "operating"]).default("operating"),
});

export const projectInput = z.object({
  /** Obligatoria: todo proyecto pertenece a una empresa (B-Green, qiro, iaworkers…). */
  companyId: z.uuid(),
  slug: z.string().regex(/^[a-z0-9-]{2,32}$/),
  keyPrefix: z.string().regex(/^[A-Z]{2,5}$/),
  name: z.string().min(2).max(80),
  description: z.string().max(500).default(""),
  dataClass: z.enum(DATA_CLASSES).default("none"),
});

export const repositoryInput = z.object({
  projectId: z.uuid(),
  fullName: z.string().regex(/^[\w.-]+\/[\w.-]+$/),
  defaultBranch: z.string().default("main"),
});

export const repositoryPatch = z.object({
  authorized: z.boolean().optional(),
  autoMergeEnabled: z.boolean().optional(),
  autoMergeClasses: z.array(z.enum(CHANGE_CLASSES)).optional(),
  fastTrackEnabled: z.boolean().optional(),
  fastTrackPaths: z.array(z.string()).optional(),
  mergeViaApi: z.boolean().optional(),
  requireSecondApprover: z.boolean().optional(),
  minRisk: z.enum(RISK_LEVELS).optional(),
  concurrencyLimit: z.number().int().min(1).max(10).optional(),
  targetBranches: z.array(z.string()).optional(),
  /** Revisión independiente (Fase 3): `off` por defecto; `shadow` registra y muestra sin bloquear ni cambiar aprobaciones. */
  reviewMode: z.enum(REVIEW_MODES).optional(),
});

export const policyRuleInput = z.object({
  scope: z.enum(["project", "repository"]),
  projectId: z.uuid().optional(),
  repositoryId: z.uuid().optional(),
  kind: z.enum(["path_glob", "keyword", "floor"]),
  name: z.string().min(2).max(120),
  params: z.record(z.string(), z.unknown()),
  minLevel: z.enum(["standard", "high", "unknown"]),
  reason: z.string().min(2).max(300),
});

export const budgetInput = z.object({
  scope: z.enum(["global", "company", "project", "mission"]),
  companyId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  missionId: z.uuid().optional(),
  period: z.enum(["monthly", "lifetime"]).default("monthly"),
  unit: z.enum(["tokens", "money"]),
  limitValue: z.number().positive(),
  currency: z.string().length(3).optional(),
  alertPct: z.number().int().min(1).max(100).default(80),
  hardStop: z.boolean().default(true),
});

export const modelPriceInput = z.object({
  provider: z.string().default("cursor"),
  model: z.string().min(1),
  inputPerMtok: z.number().nonnegative(),
  outputPerMtok: z.number().nonnegative(),
  cacheReadPerMtok: z.number().nonnegative().default(0),
  cacheWritePerMtok: z.number().nonnegative().default(0),
  currency: z.string().length(3),
  source: z.string().min(3).max(300),
});

export const billingImportInput = z.object({
  periodStart: z.iso.datetime(),
  periodEnd: z.iso.datetime(),
  amount: z.number().nonnegative(),
  currency: z.string().length(3),
  note: z.string().max(300).optional(),
  projectId: z.uuid().optional(),
});

export const userInput = z.object({
  email: z.email(),
  name: z.string().min(2).max(80),
  role: z.enum(ROLES),
  /** Opcional: sin contraseña la cuenta solo entra por Google. */
  password: z.string().min(12).max(200).optional(),
});

/** ---------- DTOs de lectura (formas que consume el frontend) ---------- */

export interface UserDTO {
  id: string;
  email: string;
  name: string;
  role: (typeof ROLES)[number];
}

export interface MissionCardDTO {
  id: string;
  key: string;
  title: string;
  status: MissionStatus;
  stage: Stage;
  indicator: Indicator | null;
  blockedReason: string | null;
  flow: (typeof FLOWS)[number] | null;
  riskLevel: (typeof RISK_LEVELS)[number];
  priority: (typeof PRIORITIES)[number];
  projectId: string;
  projectName: string;
  companyId: string;
  companyName: string;
  repositoryId: string;
  repositoryName: string;
  assigneeName: string | null;
  agentLabel: string;
  agentRunning: boolean;
  totalTokens: number;
  estimatedCost: { amount: number; currency: string } | null;
  prNumber: number | null;
  prUrl: string | null;
  updatedAt: string;
  needsAttention: "plan" | "delivery" | "blocked" | "error" | null;
}

export interface CostBreakdownDTO {
  tokens: { input: number; output: number; cacheRead: number; cacheWrite: number; total: number; measured: true };
  estimatedCost: { amount: number; currency: string; basis: string } | null;
  billed: { amount: number; currency: string; source: string } | null;
  pendingSync: boolean;
  source: string;
  collectedAt: string | null;
  completeness: "complete" | "partial" | "pending" | "none";
}

export type { PlanningSummary, DeliverySummary };

export const STATUS_LABELS_ES: Record<MissionStatus, string> = {
  pending: "Pendiente",
  planning: "Planificando",
  awaiting_plan_approval: "Esperando aprobación del plan",
  developing: "En desarrollo",
  awaiting_ci: "Esperando CI",
  in_review: "Esperando aprobación de entrega",
  merging: "Esperando merge",
  done: "Terminada",
  blocked: "Bloqueada",
  error: "Error",
  awaiting_budget: "Esperando presupuesto",
  cancelled: "Cancelada",
};
export { MISSION_STATUSES };
